import classNames from 'classnames';
import React, { useMemo, useState } from 'react';
import { RxApplicationDetails, RxRule } from '../../../tasks/types/rx';
import {
  dateToString,
  numberToOrdinal,
} from '../../utilities/formattingUtilities';
import {
  countBySeverity,
  groupByDrugAndSeverity,
  RxGroup,
  Severity,
  SortColumn,
  SortDirection,
  sortGroups,
} from './rxAggregation';
import styles from './RxHistoryV2.module.scss';

const renderHTML = (rawHTML: string): string => {
  if (!rawHTML) return '';
  const doc = new DOMParser().parseFromString(rawHTML, 'text/html');
  return doc.documentElement.textContent || '';
};

const ACTIVATE_KEYS = ['Enter', ' '];

const onActivateKeyDown = (handler: () => void) => (e: React.KeyboardEvent) => {
  if (ACTIVATE_KEYS.includes(e.key)) {
    e.preventDefault();
    handler();
  }
};

type FilterValue = 'all' | 'high' | 'medium' | 'low';

const FILTER_SEVERITY: Record<Exclude<FilterValue, 'all'>, Severity> = {
  high: 'HIGH',
  medium: 'MEDIUM',
  low: 'LOW',
};

const formatHeaderTimestamp = (now: Date): string => {
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const yyyy = now.getFullYear();
  const hours24 = now.getHours();
  const hours = hours24 % 12 || 12;
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const ampm = hours24 >= 12 ? 'PM' : 'AM';
  const tzMinutes = -now.getTimezoneOffset();
  const tzSign = tzMinutes >= 0 ? '+' : '-';
  const tzAbs = Math.abs(tzMinutes);
  const tzH = String(Math.floor(tzAbs / 60)).padStart(2, '0');
  const tzM = String(tzAbs % 60).padStart(2, '0');
  const tz =
    tzM === '00' ? `${tzSign}${parseInt(tzH, 10)}` : `${tzSign}${tzH}:${tzM}`;
  return `${mm}/${dd}/${yyyy} · ${hours}:${minutes} ${ampm} ${tz}`;
};

const sevPillClass = (severity: Severity): string =>
  severity === 'HIGH'
    ? classNames(styles.SevPill, styles.SevPillHigh)
    : severity === 'MEDIUM'
      ? classNames(styles.SevPill, styles.SevPillMedium)
      : classNames(styles.SevPill, styles.SevPillLow);

const filterButtonClass = (
  filterValue: FilterValue,
  active: FilterValue,
): string => {
  if (filterValue !== active) return styles.FilterBtn;
  if (filterValue === 'medium') {
    return classNames(styles.FilterBtn, styles.FilterBtnMediumActive);
  }
  if (filterValue === 'high') {
    return classNames(styles.FilterBtn, styles.FilterBtnHighActive);
  }
  return classNames(styles.FilterBtn, styles.FilterBtnActive);
};

const SortIcon = ({
  active,
  direction,
}: {
  active: boolean;
  direction: SortDirection;
}) => (
  <span className={styles.SortIcon}>
    <span
      className={
        active && direction === 'asc' ? styles.SortIconActiveUp : undefined
      }
    >
      ▲
    </span>
    <span
      className={
        active && direction === 'desc' ? styles.SortIconActiveDown : undefined
      }
    >
      ▼
    </span>
  </span>
);

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);
  const showCopied = () => {
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!navigator.clipboard?.writeText) {
      return;
    }
    navigator.clipboard.writeText(text).then(showCopied, () => {
      // Clipboard write rejected — leave the button in idle state so the user
      // doesn't see a misleading "Copied" confirmation when nothing was copied.
    });
  };
  return (
    <button
      type="button"
      className={classNames(styles.CopyBtn, copied && styles.CopyBtnCopied)}
      onClick={handleClick}
    >
      {copied ? '✓ Copied' : '⎘ Copy'}
    </button>
  );
};

const buildCopyText = (group: RxGroup): string => {
  if (group.isSingleFill) {
    return `${group.drugName}, ${dateToString(group.latestFillDate)}`;
  }
  return `${group.drugName}, ${dateToString(group.earliestFillDate)} - ${dateToString(group.latestFillDate)}`;
};

const MedRow = ({
  group,
  open,
  onToggle,
}: {
  group: RxGroup;
  open: boolean;
  onToggle: () => void;
}) => {
  const physicianCount = group.physicians.length;

  const indicationsPreview = group.indications.slice(0, 2);
  const indicationsMore = group.indications.length - indicationsPreview.length;

  return (
    <div className={classNames(styles.MedRow, open && styles.MedRowOpen)}>
      <div className={styles.MedMain}>
        <div className={classNames(styles.MedCell, styles.MedNameCell)}>
          <div className={styles.MedNameWrap}>
            <span
              className={classNames(
                styles.MedName,
                group.severity === 'HIGH' && styles.MedNameHigh,
              )}
              role="button"
              tabIndex={0}
              aria-expanded={open}
              onClick={onToggle}
              onKeyDown={onActivateKeyDown(onToggle)}
            >
              {group.drugName}
              {physicianCount > 1 && (
                <span className={styles.MultiPhysFlag}>
                  {physicianCount} physicians
                </span>
              )}
            </span>
            <CopyButton text={buildCopyText(group)} />
          </div>
        </div>
        <div className={styles.MedCell}>
          <span className={sevPillClass(group.severity)}>{group.severity}</span>
        </div>
        <div className={styles.MedCell}>
          <div className={styles.FillCompact}>
            <span className={styles.FillLatest}>
              {dateToString(group.latestFillDate)}
            </span>
            <span className={styles.FillRange}>
              {group.isSingleFill
                ? 'Single fill'
                : `${dateToString(group.earliestFillDate)} – ${dateToString(group.latestFillDate)}`}
            </span>
          </div>
        </div>
        <div className={styles.MedCell}>
          <span className={styles.FillsCount}>{group.totalFills}</span>
        </div>
        <div className={styles.MedCell}>
          <span className={styles.QtyCompact}>
            {group.varies ? 'Varies' : group.quantity}
            <br />
            <span className={styles.QtySub}>
              {group.varies ? 'per fill' : `${group.days} days`}
            </span>
          </span>
        </div>
        <div className={styles.MedCell}>
          <div className={styles.IndList}>
            {indicationsPreview.map(ind => (
              <span key={ind} className={styles.IndTag}>
                {renderHTML(ind)}
              </span>
            ))}
            {indicationsMore > 0 && (
              <span className={styles.IndMore}>+{indicationsMore} more</span>
            )}
          </div>
        </div>
      </div>

      {open && (
        <div className={styles.MedDetail}>
          <div className={styles.DetailInner}>
            <div className={styles.DetailSection}>
              <div className={styles.DetailSectionLabel}>Physician & Fills</div>
              <div className={styles.PhysicianBlockStacked}>
                {group.physicians.map((p, idx) => {
                  const totalForBlock = p.fills.length;
                  return (
                    <div
                      key={`${p.doctor.name}-${p.doctor.address}-${idx}`}
                      className={styles.PhysicianBlock}
                    >
                      {physicianCount > 1 && (
                        <div className={styles.PhysicianBlockNum}>
                          Physician {idx + 1} · {totalForBlock} fill
                          {totalForBlock === 1 ? '' : 's'}
                        </div>
                      )}
                      <div className={styles.PhysicianSpec}>
                        {renderHTML(p.doctor.speciality)}
                      </div>
                      <div
                        className={classNames(
                          styles.PhysicianLine,
                          styles.PhysicianName,
                        )}
                      >
                        {renderHTML(p.doctor.name)}
                      </div>
                      <div className={styles.PhysicianLine}>
                        {renderHTML(p.doctor.address)}
                      </div>
                      <div className={styles.PhysicianLine}>
                        {renderHTML(p.doctor.phone)}
                      </div>
                      <div className={styles.FillList}>
                        {p.fills.map((fill, fillIdx) => {
                          const ordinal = totalForBlock - fillIdx;
                          return (
                            <div
                              key={`${fill.date}-${fillIdx}`}
                              className={styles.FillItem}
                            >
                              <span className={styles.FillN}>
                                {numberToOrdinal(ordinal)}
                              </span>
                              <span>{dateToString(fill.date)}</span>
                              <span className={styles.FillSep}>·</span>
                              <span className={styles.FillQty}>
                                {fill.quantity}
                              </span>
                              <span className={styles.FillDays}>
                                {fill.days} days
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className={styles.DetailSection}>
              <div className={styles.DetailSectionLabel}>All Indications</div>
              <div className={styles.IndDetailList}>
                {group.indications.map(ind => (
                  <div key={ind} className={styles.IndDetailItem}>
                    {renderHTML(ind)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const RxHistoryV2 = ({
  rxDetail,
}: {
  rxDetail: RxApplicationDetails;
}) => {
  const [filter, setFilter] = useState<FilterValue>('all');
  const [sortConfig, setSortConfig] = useState<{
    column: SortColumn;
    direction: SortDirection;
  }>({ column: 'date', direction: 'desc' });
  const [irixOpen, setIrixOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const toggleRow = (rowKey: string) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowKey]: !prev[rowKey],
    }));
  };

  const allGroups = useMemo(
    () => groupByDrugAndSeverity(rxDetail.rx || []),
    [rxDetail.rx],
  );

  const counts = useMemo(() => countBySeverity(allGroups), [allGroups]);

  const visibleGroups = useMemo(() => {
    const filtered =
      filter === 'all'
        ? allGroups
        : allGroups.filter(g => g.severity === FILTER_SEVERITY[filter]);
    return sortGroups(filtered, sortConfig.column, sortConfig.direction);
  }, [allGroups, filter, sortConfig]);

  const allVisibleExpanded = useMemo(() => {
    if (visibleGroups.length === 0) return false;
    return visibleGroups.every(
      g => !!expandedRows[`${g.drugName}-${g.severity}`],
    );
  }, [visibleGroups, expandedRows]);

  const handleExpandCollapseAll = () => {
    if (allVisibleExpanded) {
      setExpandedRows(prev => {
        const next = { ...prev };
        visibleGroups.forEach(g => {
          next[`${g.drugName}-${g.severity}`] = false;
        });
        return next;
      });
    } else {
      setExpandedRows(prev => {
        const next = { ...prev };
        visibleGroups.forEach(g => {
          next[`${g.drugName}-${g.severity}`] = true;
        });
        return next;
      });
    }
  };

  const onSort = (column: SortColumn) => {
    setSortConfig(curr => {
      if (curr.column === column) {
        return {
          column,
          direction: curr.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { column, direction: 'asc' };
    });
  };

  const headerTimestamp = useMemo(() => formatHeaderTimestamp(new Date()), []);
  const rxRules: RxRule[] = rxDetail.rxRules || [];

  return (
    <div className={styles.Panel} data-label="rx-history-v2">
      <div className={styles.PanelHeader}>
        <span className={styles.PanelTitle}>Rx History</span>
        <span className={styles.PanelMeta}>
          {headerTimestamp} · Risk Score:{' '}
          <strong>{rxDetail.riskScore ?? '—'}</strong>
        </span>
      </div>

      <div className={styles.FilterBar}>
        <span className={styles.FilterLabel}>Severity:</span>
        <button
          type="button"
          className={filterButtonClass('all', filter)}
          onClick={() => setFilter('all')}
        >
          All ({counts.all})
        </button>
        <button
          type="button"
          className={filterButtonClass('high', filter)}
          onClick={() => setFilter('high')}
        >
          High ({counts.high})
        </button>
        <button
          type="button"
          className={filterButtonClass('medium', filter)}
          onClick={() => setFilter('medium')}
        >
          Medium ({counts.medium})
        </button>
        <button
          type="button"
          className={filterButtonClass('low', filter)}
          onClick={() => setFilter('low')}
        >
          Low ({counts.low})
        </button>
        {visibleGroups.length > 0 && (
          <button
            type="button"
            className={styles.ExpandAllBtn}
            onClick={handleExpandCollapseAll}
          >
            {allVisibleExpanded ? 'Collapse All' : 'Expand All'}
          </button>
        )}
      </div>

      <div className={styles.TableHeader} role="row">
        <div className={styles.Th}>Medication</div>
        <div
          className={classNames(styles.Th, styles.ThSortable)}
          role="button"
          tabIndex={0}
          aria-label="Sort by severity"
          aria-pressed={sortConfig.column === 'severity'}
          onClick={() => onSort('severity')}
          onKeyDown={onActivateKeyDown(() => onSort('severity'))}
        >
          Severity
          <SortIcon
            active={sortConfig.column === 'severity'}
            direction={sortConfig.direction}
          />
        </div>
        <div
          className={classNames(styles.Th, styles.ThSortable)}
          role="button"
          tabIndex={0}
          aria-label="Sort by latest fill date"
          aria-pressed={sortConfig.column === 'date'}
          onClick={() => onSort('date')}
          onKeyDown={onActivateKeyDown(() => onSort('date'))}
        >
          Latest Fill Range
          <SortIcon
            active={sortConfig.column === 'date'}
            direction={sortConfig.direction}
          />
        </div>
        <div className={styles.Th}>Fills</div>
        <div className={styles.Th}>Qty</div>
        <div
          className={classNames(styles.Th, styles.ThSortable)}
          role="button"
          tabIndex={0}
          aria-label="Sort by indication"
          aria-pressed={sortConfig.column === 'indication'}
          onClick={() => onSort('indication')}
          onKeyDown={onActivateKeyDown(() => onSort('indication'))}
        >
          Indications
          <SortIcon
            active={sortConfig.column === 'indication'}
            direction={sortConfig.direction}
          />
        </div>
      </div>

      {visibleGroups.length === 0 ? (
        <div className={styles.EmptyState}>
          No medications match this filter.
        </div>
      ) : (
        visibleGroups.map(group => {
          const rowKey = `${group.drugName}-${group.severity}`;
          return (
            <MedRow
              key={rowKey}
              group={group}
              open={!!expandedRows[rowKey]}
              onToggle={() => toggleRow(rowKey)}
            />
          );
        })
      )}

      <div className={styles.IrixDivider} />

      <div
        className={classNames(
          styles.IrixSection,
          irixOpen && styles.IrixSectionOpen,
        )}
      >
        <div
          className={styles.IrixHeader}
          role="button"
          tabIndex={0}
          aria-expanded={irixOpen}
          onClick={() => setIrixOpen(o => !o)}
          onKeyDown={onActivateKeyDown(() => setIrixOpen(o => !o))}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className={styles.IrixTitle}>IRIX Rules</span>
            <span className={styles.IrixCount}>
              {rxRules.length} rule{rxRules.length === 1 ? '' : 's'}
            </span>
          </div>
          <span className={styles.IrixChevron}>▼</span>
        </div>
        {irixOpen && (
          <div className={styles.IrixBody}>
            {rxRules.length === 0 ? (
              <div className={styles.EmptyState}>No IRIX rules.</div>
            ) : (
              rxRules.map(rule => (
                <div key={rule.ruleId} className={styles.IrixRow}>
                  <span className={styles.IrixRowNum}>{rule.ruleId}</span>
                  <span>{rule.name}</span>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};
